import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useQuery } from '@apollo/client';
import { useTranslation } from 'react-i18next';
import { Segmented, Alert, Spin, Empty } from 'antd';
import dayjs from 'dayjs';
import { useWarehouseContext } from '../../../contexts/WarehouseContext';
import { SiteType } from '../../../graphql/types/SiteType';
import { HUB_DAILY_REVIEW } from './graphql/queries/hubDailyReview';
import { HUB_ASSIGNED_TASKS } from './graphql/queries/hubAssignedTasks';
import HubWarehouseCard from './components/HubWarehouseCard';
import AssignTaskPanel from './components/AssignTaskPanel';
import AssignedTaskList from './components/AssignedTaskList';
import { formatCalculatedAtTime } from './utils/dataUpdatedAt';

const ASSIGNED_DAY_RANGE = 7;

// 口径同仓管工作台（MyWorkbenchPage）：手动刷新按钮已去掉，页面靠 5 分钟轮询保鲜
const POLL_INTERVAL = 5 * 60 * 1000;

/**
 * 需关注 / 全部 两个筛选在前端做。
 *
 * 后端 2026-09-03 起把全部仓一并按 toWarehouseReview 全字段下发（形态统一大卡片，
 * 不再拆需关注 / 运行正常）；「需关注」筛选是「日报截止后仍有未处理周期风险」的仓，
 * 「全部」筛选就是原样出所有仓。
 */
const FILTER_NEEDS_ATTENTION = 'NEEDS_ATTENTION';

/** 空态文案跟着筛选走：两个 tab 各有各的「为什么这里没东西」，共用一句会说反 */
const EMPTY_DESC_KEY: Record<string, string> = {
  [FILTER_NEEDS_ATTENTION]: 'hubReview.noWarehousesNeedAttention',
};
const EMPTY_DESC_KEY_ALL = 'common.noData';

const byUnfinishedDesc = (a: any, b: any): number =>
  b.unfinishedTaskCount - a.unfinishedTaskCount;

const HubDailyReviewPage: React.FC = () => {
  const { t } = useTranslation();
  const [filter, setFilter] = useState<string>(FILTER_NEEDS_ATTENTION);
  // hubId 取左侧树当前选中的片区，不从登录人角色推导 —— 总部账号不绑定单一片区，
  // 推导会把它们挡在页面外。越权由后端 @RequireResourceAndWarehouse 按仓库权限拦截
  const { currentWarehouse } = useWarehouseContext();
  const [isVisible, setIsVisible] = useState(true);
  // 选中项是 WHS 时取其父片区（口径同 WorkProgressPage）：选中的仓库跨页面共用一个
  // localStorage key，在仓管工作台选了 WHS 再回本页刷新就会带着 WHS id 进来，
  // 直接当 hubId 发出去后端会报错
  const hubId = useMemo(() => {
    if (!currentWarehouse) return undefined;
    if (currentWarehouse.siteType === SiteType.HUB) {
      return Number(currentWarehouse.id);
    }
    if (currentWarehouse.siteType === SiteType.WAREHOUSE && currentWarehouse.parentSiteId) {
      return Number(currentWarehouse.parentSiteId);
    }
    return undefined;
  }, [currentWarehouse]);

  useEffect(() => {
    const handleVisibilityChange = () => {
      setIsVisible(document.visibilityState === 'visible');
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, []);

  const {
    data: reviewRaw,
    loading: reviewLoading,
    error: reviewError,
    refetch: refetchReview,
    startPolling: startPollingReview,
    stopPolling: stopPollingReview,
  } = useQuery(HUB_DAILY_REVIEW, {
    variables: { hubId },
    skip: !hubId,
    pollInterval: POLL_INTERVAL,
  });

  const {
    data: assignedRaw,
    refetch: refetchAssigned,
    startPolling: startPollingAssigned,
    stopPolling: stopPollingAssigned,
  } = useQuery(HUB_ASSIGNED_TASKS, {
    variables: { hubId, dayRange: ASSIGNED_DAY_RANGE },
    skip: !hubId,
    pollInterval: POLL_INTERVAL,
  });

  // 页面切到后台就停轮询，避免无人看时白拉数据；回到前台先补一次再恢复。
  // 复盘与派发列表是两条独立查询，轮询各自控制
  useEffect(() => {
    if (!hubId) return;
    if (!isVisible) {
      stopPollingReview();
    } else {
      refetchReview();
      startPollingReview(POLL_INTERVAL);
    }
  }, [isVisible, hubId, stopPollingReview, refetchReview, startPollingReview]);

  useEffect(() => {
    if (!hubId) return;
    if (!isVisible) {
      stopPollingAssigned();
    } else {
      refetchAssigned();
      startPollingAssigned(POLL_INTERVAL);
    }
  }, [isVisible, hubId, stopPollingAssigned, refetchAssigned, startPollingAssigned]);

  const review = reviewRaw?.hubDailyReview;
  const assigned = assignedRaw?.hubAssignedTasks;
  const isLoading = reviewLoading;

  const handleRefresh = useCallback(() => {
    refetchReview();
    refetchAssigned();
  }, [refetchReview, refetchAssigned]);

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <Spin size="large" />
      </div>
    );
  }

  if (reviewError) {
    return (
      <Alert
        message={String(t('common.error'))}
        description={String(t('common.errorOccurred'))}
        type="error"
        showIcon
      />
    );
  }

  if (!review) {
    return <Empty description={String(t('common.noData'))} />;
  }

  const { summary, taskCompletion } = review;

  const parsedDate = dayjs(review.reviewDate);
  const weekdays = t('workbench.weekdays', { returnObjects: true }) as unknown as string[];
  const weekdayText = Array.isArray(weekdays) ? weekdays[parsedDate.day()] : '';
  const dateText = parsedDate.isValid()
    ? parsedDate.format(String(t('workbench.dateFormat')))
    : review.reviewDate;

  // 「全部」tab：所有仓都出大卡片（后端已按 toWarehouseReview 全字段下发，不再拆 onTrack）；
  // 「按未完成任务数排序」的提示要名副其实只能本地排
  const allWarehouses = [...review.warehouses].sort(byUnfinishedDesc);
  // 「需关注」tab：deadline 前一律不展示（日报未截止就用 unresolvedPeriodRisks 判定不成立）；
  // 到点后只留仍有未处理周期风险的仓
  const reportDeadlinePassed = !review.reportDeadline
    || Date.parse(review.serverNow) >= Date.parse(review.reportDeadline);
  const needsAttentionWarehouses = reportDeadlinePassed
    ? allWarehouses.filter((w: any) => (w.unresolvedPeriodRisks?.length ?? 0) > 0)
    : [];
  // 「需关注」为 0 时整条 tab 不出：常驻空 tab 会误导 hub manager 反复点回来看，
  // 不如直接不给这个入口。此时 filter 若仍是 NEEDS_ATTENTION（用户初始状态或轮询前留的），
  // 按「全部」渲染，等有数据再出 tab。
  // 复盘（PERIOD）灰度关掉时也整条隐藏 ——「需关注」筛的依据是 unresolvedPeriodRisks（仅 PERIOD 源），
  // PERIOD 不展示后，仓卡右侧只出 LIVE 条目，用户点进「需关注」看不到筛选依据，tab 名不副实
  const showNeedsAttentionTab = review.showPeriod
    && needsAttentionWarehouses.length > 0;
  const effectiveFilter = showNeedsAttentionTab ? filter : 'ALL';

  // headline 由前端拼：后端 HubDailyReviewType 无此字段，三个数都能从已返回的汇总值算出，
  // 且文案需要 i18n
  // 未交仓名只点名第一个，其余折成「等 N 个」：后端不截断这个列表，
  // 片区下十几个仓都没交时整行会被省略号切掉，反而谁都看不到
  // 「等 N 个」直接取分母−分子（未交总数），首个仓也计入 —— 与右上「0/5」的分母口径一致
  const reportPendingNames: string[] = summary.reportPendingWarehouseNames;
  const reportPendingCount = summary.reportsExpected - summary.reportsIn;
  const reportPendingText = reportPendingCount > 1
    ? `${reportPendingNames[0]} ${String(t('hubReview.reportPendingMore', {
      count: reportPendingCount,
    }))}`
    : reportPendingNames[0];

  // 只数灰度仓：非灰度仓不生成 SOP 任务，算进来会让「N 个仓共 X 项未完成」的分母虚高。
  // 下面筛选条的「全部 N 仓」是另一个口径（管渲染多少张卡），故不共用这个数
  const totalWarehouseCount = (review.assignableWarehouses ?? []).length;
  // 上游 riskUnresolved 是 LIVE + PERIOD 合并未处理数，不按灰度拆分；PERIOD 未处理可由
  // 每仓 unresolvedPeriodRisks 求和拿到（on-track 仓按定义无未处理，不参与），LIVE 未处理 = 合并 − PERIOD。
  // 与仓卡右侧「风险及处理情况 · N」的过滤后计数口径对齐，避免顶部与仓卡数字打架
  const sumPeriodUnresolved = (review.warehouses ?? [])
    .reduce((sum: number, w: any) => sum + (w.unresolvedPeriodRisks?.length ?? 0), 0);
  const risksUnresolvedForDisplay = (() => {
    if (review.showLive && review.showPeriod) return summary.risksUnresolved;
    if (review.showLive) return Math.max(0, summary.risksUnresolved - sumPeriodUnresolved);
    if (review.showPeriod) return sumPeriodUnresolved;
    // 两栏都关时字段整格隐藏，此处返回什么都不会被渲染
    return 0;
  })();
  // 灰度全关时 headline 不能再喊「N 条风险待你决策」—— 与下方汇总条隐藏口径一致
  const showRisksInHeadline = (review.showLive || review.showPeriod)
    && risksUnresolvedForDisplay > 0;
  const headline = showRisksInHeadline
    ? t('hubReview.headlineWithRisks', {
      warehouseCount: totalWarehouseCount,
      taskCount: summary.unfinishedTasks,
      riskCount: risksUnresolvedForDisplay,
    })
    : t('hubReview.headline', {
      warehouseCount: totalWarehouseCount,
      taskCount: summary.unfinishedTasks,
    });


  return (
    <div className="bg-white rounded-lg shadow-sm">
      {/* 1 页头：面包屑 + headline + 片区完成度 */}
      <div className="flex items-start justify-between gap-6 pt-5 pb-4 bg-gradient-to-r from-violet-50 to-purple-50 rounded-t-lg">
        <div className="min-w-0">
          <p className="text-xs text-gray-400">
            {review.hubName} · {String(t('hubReview.title'))} · {dateText} {weekdayText}
          </p>
          <h1 className="text-2xl font-bold text-gray-900 mt-1">{String(headline)}</h1>
        </div>
        <div className="flex items-start gap-3 shrink-0">
          <div className="text-right pr-6">
            <p className="text-xs text-gray-400">{String(t('hubReview.hubCompletion'))}</p>
            <p className="text-2xl font-bold text-violet-600 mt-1">
              {taskCompletion.doneCount} / {taskCompletion.totalCount} · {taskCompletion.percent}%
            </p>
            <div className="h-1.5 w-56 rounded-full bg-gray-200 overflow-hidden mt-1.5">
              <div
                className="h-full rounded-full bg-emerald-500"
                style={{ width: `${taskCompletion.percent}%` }}
              />
            </div>
          </div>
        </div>
      </div>

      {/* 2 汇总条 */}
      <div className="flex items-center divide-x divide-violet-200 border-y border-violet-200">
        <div className="flex-1 min-w-0 pl-2 pr-6 py-3 first:pl-0 last:pr-0 flex items-baseline gap-2">
          <span className="text-sm text-gray-600 shrink-0">
            {String(t('hubReview.unfinishedTasks'))}
          </span>
          <span className="text-xl font-bold text-red-600">{summary.doUnfinishedTasks}</span>
          {summary.overdueTasks > 0 && (
            <span className="text-xs text-gray-500">
              {summary.overdueTasks} {String(t('hubReview.overdue'))}
            </span>
          )}
        </div>

        {/* 「未处理风险」两块灰度都关时整格隐藏 —— 与仓卡右侧「风险及处理情况」的隐藏口径一致；
            上游未按 LIVE / PERIOD 拆分未处理数，故只做「全关即隐藏」，任一开则仍展示合并数 */}
        {(review.showLive || review.showPeriod) && (
        <div className="flex-1 min-w-0 pl-2 pr-6 py-3 first:pl-0 last:pr-0 flex items-baseline gap-2">
          <span className="text-sm text-gray-600 shrink-0">
            {String(t('hubReview.risksUnresolved'))}
          </span>
          <span className={`text-xl font-bold ${risksUnresolvedForDisplay > 0 ? 'text-red-600' : 'text-gray-900'}`}>
            {risksUnresolvedForDisplay}
          </span>
          {summary.risksInProgress > 0 && (
            <span className="text-xs text-gray-500">
              {String(t('hubReview.risksInProgressCount', { count: summary.risksInProgress }))}
            </span>
          )}
        </div>
        )}

        <div className="flex-1 min-w-0 pl-2 pr-6 py-3 first:pl-0 last:pr-0 flex flex-wrap items-baseline gap-x-2 gap-y-1">
          <span className="text-sm text-gray-600 shrink-0">
            {String(t('hubReview.myAssigned'))}
          </span>
          <span className="text-xl font-bold text-gray-900 whitespace-nowrap">
            {summary.myAssignedDone} / {summary.myAssignedTotal}
          </span>
          <span className="text-xs text-gray-500">
            {String(t('hubReview.myAssignedDoneSuffix'))}
          </span>
          {(summary.myAssignedOverdue ?? 0) > 0 && (
            <span className="text-xs text-red-500">
              {String(t('hubReview.assigned.overdueCount', { count: summary.myAssignedOverdue }))}
            </span>
          )}
        </div>

        {/* 这格还要塞未交仓名与「数据更新于 + 时分秒」，等分四份会把它挤成两行，故多分一点宽度 */}
        <div className="flex-[1.5] min-w-0 pl-2 pr-6 py-3 first:pl-0 last:pr-0 flex items-baseline justify-between gap-3">
          <span className="flex flex-wrap items-baseline gap-x-2 gap-y-1 min-w-0">
            <span className="text-sm text-gray-600 shrink-0">
              {String(t('hubReview.reportsIn'))}
            </span>
            <span className="text-xl font-bold text-gray-900 whitespace-nowrap">
              {summary.reportsIn} / {summary.reportsExpected}
            </span>
            {reportPendingNames.length > 0 && (
              <span className="text-xs text-gray-500 truncate">
                {String(t('hubReview.reportPendingNames', { names: reportPendingText }))}
              </span>
            )}
          </span>
          {/* 排版口径同 MetricsBar 的断更格：标签与时分秒分两行，
              单行带日期的写法会把左侧未交仓名挤成省略号 */}
          {review.calculatedAt && (
            <span className="text-xs text-red-500 shrink-0 text-right leading-tight">
              {String(t('workbench.dataUpdated'))}
              <br />
              {formatCalculatedAtTime(review.calculatedAt, review.timeZone) || '—'}
            </span>
          )}
        </div>
      </div>

      {/* 3 主体：左按仓复盘 / 右派发 */}
      <div className="grid grid-cols-1 xl:grid-cols-8 gap-6 py-6 items-start">
        <div className="xl:col-span-5">
          <div className="flex flex-wrap items-center gap-x-4 gap-y-2 mb-4">
            <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
              <h2 className="text-base font-bold shrink-0">{String(t('hubReview.byWarehouse'))}</h2>
              <Segmented
                size="small"
                // antd 默认皮肤是灰底轨道 + 白色选中块，设计稿要白底轨道 + 浅紫选中块。
                // thumb 一并染色，否则切换动画中途会闪一下白块
                className="!bg-white [&_.ant-segmented-item]:text-gray-600 [&_.ant-segmented-item-selected]:!bg-violet-100 [&_.ant-segmented-item-selected]:!text-violet-600 [&_.ant-segmented-item-selected]:!font-medium [&_.ant-segmented-item-selected]:!shadow-none [&_.ant-segmented-thumb]:!bg-violet-100"
                value={effectiveFilter}
                onChange={(value) => setFilter(String(value))}
                options={[
                  ...(showNeedsAttentionTab ? [{
                    label: `${String(t('hubReview.filter.needsAttention'))} · ${needsAttentionWarehouses.length}`,
                    value: 'NEEDS_ATTENTION',
                  }] : []),
                  {
                    label: String(t('hubReview.filter.all', {
                      count: allWarehouses.length,
                    })),
                    value: 'ALL',
                  },
                ]}
              />
            </div>
            <span className="ml-auto text-xs text-gray-400 shrink-0">
              {String(t('hubReview.sortHint'))}
            </span>
          </div>

          {(() => {
            const shown = effectiveFilter === FILTER_NEEDS_ATTENTION ? needsAttentionWarehouses : allWarehouses;
            if (shown.length === 0) {
              return <Empty description={String(t(EMPTY_DESC_KEY[effectiveFilter] ?? EMPTY_DESC_KEY_ALL))} />;
            }
            return shown.map((warehouse: any) => (
              <HubWarehouseCard
                key={warehouse.warehouseId}
                warehouse={warehouse}
                serverNow={review.serverNow}
                reportDeadline={review.reportDeadline}
                showLive={review.showLive}
                showPeriod={review.showPeriod}
              />
            ));
          })()}
        </div>

        <div className="xl:col-span-3">
          {/* 可派仓由后端按上游灰度范围下发，不再用侧边栏那棵树的全量子仓：
              非灰度仓不生成 SOP 任务，派进去在那个仓的工作台里不显示 */}
          {/* 不传 timeZone —— 派发面板按选中仓的墙钟算截止时刻。
              同 hub 下 LA 与 LA_DC 时区可以不同，若用 hub 级 tz，切到 LA_DC 后墙钟会整体偏一个时区差 */}
          <AssignTaskPanel
            hubId={review.hubId}
            warehouses={review.assignableWarehouses ?? []}
            recentTitles={assigned?.recentTitles ?? []}
            onAssigned={handleRefresh}
          />
          <AssignedTaskList
            tasks={assigned?.tasks ?? []}
            dayRange={ASSIGNED_DAY_RANGE}
            totalInRange={assigned?.totalInRange ?? 0}
          />
        </div>
      </div>
    </div>
  );
};

export default HubDailyReviewPage;
